import React from "react";
import { MdEdit, MdDelete, MdStar } from "react-icons/md";

const RoleTable = ({ roles, loading, onEdit, onDelete }) => {
  // Fonction pour obtenir la classe de badge en fonction du niveau de privilège
  const getPrivilegeBadge = (privilege) => {
    const privilegeClasses = {
      1: "bg-purple-100 text-purple-800 border-purple-200",  // Admin (niveau le plus élevé)
      2: "bg-blue-100 text-blue-800 border-blue-200",        // Niveau 2
      3: "bg-green-100 text-green-800 border-green-200",     // Niveau 3
      4: "bg-gray-100 text-gray-800 border-gray-200",        // Niveau le plus bas
    };

    const defaultClass = "bg-gray-100 text-gray-800 border-gray-200";
    return privilegeClasses[privilege] || defaultClass;
  };

  // Fonction pour obtenir le label du niveau de privilège
  const getPrivilegeLabel = (privilege) => {
    // Plus le chiffre est petit, plus le privilège est élevé
    switch (privilege) {
      case 1:
        return "Maximal";
      case 2:
        return "Élevé";
      case 3:
        return "Moyen";
      case 4:
        return "Standard";
      default:
        return `Niveau ${privilege}`;
    }
  };

  return (
    <div className="overflow-hidden rounded-lg shadow-sm border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-700">
              Nom du rôle
            </th>
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">
              Description
            </th>
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700">
              Niveau de privilège
            </th>
            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {loading && roles.length === 0 ? (
            <tr>
              <td colSpan="4" className="px-3 py-6 text-center text-sm text-gray-500">
                <div className="flex justify-center">
                  <svg className="animate-spin h-5 w-5 text-[#57A863]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </td>
            </tr>
          ) : roles.length === 0 ? (
            <tr>
              <td colSpan="4" className="px-3 py-6 text-center text-sm text-gray-500">
                <p>Aucun rôle trouvé</p>
                <p className="text-xs text-gray-400 mt-1">
                  Créez un nouveau rôle en cliquant sur le bouton "Nouveau Rôle"
                </p>
              </td>
            </tr>
          ) : (
            roles.map((role) => (
              <tr key={role.role_id} className="hover:bg-gray-50 transition-colors">
                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    {role.privilege === 1 && (
                      <MdStar className="h-5 w-5 text-yellow-500" title="Rôle administrateur" />
                    )}
                    {role.role_name}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                  {role.role_description}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPrivilegeBadge(role.privilege)}`}
                  >
                    {getPrivilegeLabel(role.privilege)}
                  </span>
                </td>
                <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onEdit(role)}
                      className="p-1.5 text-[#57A863] hover:text-[#4c9657] rounded-md hover:bg-gray-100 transition-colors"
                      title="Modifier"
                    >
                      <MdEdit className="h-5 w-5" />
                    </button>
                    
                    {/* Désactiver la suppression du rôle administrateur (privilège 1) */}
                    <button
                      onClick={() => onDelete(role)}
                      className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors ${
                        role.privilege === 1 
                          ? "text-gray-400 cursor-not-allowed" 
                          : "text-red-500 hover:text-red-700"
                      }`}
                      title={
                        role.privilege === 1 
                          ? "Le rôle administrateur ne peut pas être supprimé" 
                          : "Supprimer"
                      }
                      disabled={role.privilege === 1}
                    >
                      <MdDelete className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RoleTable;